import blockDefaults from '@apruv/survey-contract/block-defaults';
import {
  compileSurveyDocument,
  enterSurvey,
  visibleSurveyComponents,
  surveyComponentDestination,
  type CompiledSurvey,
  type PublicSurveyDocument,
  type SurveyRuntimeContext,
} from '@apruv/survey-contract/compiler';

/** Runtime invitation fields share FlowValues with component answers. */
export const RESERVED_SURVEY_VALUE_KEYS = [
  'appointmentCode',
  'cxExecutionStepId',
  'cxSurveyCode',
  'cxTriggerGroupId',
  '__proto__',
  'constructor',
  'prototype',
] as const;

export function assertCanonicalComponentIds(compiled: CompiledSurvey) {
  const reserved = new Set<string>(RESERVED_SURVEY_VALUE_KEYS);
  if (
    compiled.snapshot.screens.some((screen) =>
      screen.components.some((component) => reserved.has(component.id))
    )
  )
    throw new Error('Survey component IDs cannot use reserved runtime fields.');
}

export type SurveyPresentation =
  | { kind: 'legacy'; compiled: CompiledSurvey | null }
  | { kind: 'feedback_2026'; compiled: CompiledSurvey };

/** An explicit invalid document must never silently render its compatibility mirror. */
export function selectSurveyPresentation(response: {
  surveyDocument?: unknown;
}): SurveyPresentation {
  if (!Object.prototype.hasOwnProperty.call(response, 'surveyDocument'))
    return { kind: 'legacy', compiled: null };
  const compiled = compileSurveyDocument(response.surveyDocument as PublicSurveyDocument);
  if (
    compiled.snapshot.presentation === 'legacy' &&
    (compiled.snapshot.flags.bindings.length || compiled.snapshot.flags.guards.length)
  )
    throw new Error('Flag-bound surveys require the canonical presentation.');
  if (compiled.snapshot.presentation === 'feedback_2026') assertCanonicalComponentIds(compiled);
  return { kind: compiled.snapshot.presentation, compiled };
}

export function selectedSurveyMode(compiled: CompiledSurvey, context: SurveyRuntimeContext) {
  // The compiler owns scale validation, including the required selection for dual-mode documents.
  enterSurvey(compiled, context);
  return typeof context.score_method === 'string'
    ? context.score_method.toLowerCase()
    : compiled.snapshot.configuration.rating.mode;
}

export function visiblePresentationComponents(
  compiled: CompiledSurvey,
  screenId: string,
  context: SurveyRuntimeContext
) {
  const mode = selectedSurveyMode(compiled, context);
  return visibleSurveyComponents(compiled, screenId, context).filter(
    (component) =>
      (component.type !== 'csat_rating' || mode === 'csat') &&
      (component.type !== 'nps_rating' || mode === 'nps')
  );
}

/** Reject stale/hidden/wrong-scale interactions before the runtime persists or navigates. */
export function presentationComponentDestination(
  compiled: CompiledSurvey,
  screenId: string,
  componentId: string,
  context: SurveyRuntimeContext,
  interaction?: Parameters<typeof surveyComponentDestination>[3]
) {
  if (
    !visiblePresentationComponents(compiled, screenId, context).some(
      (component) => component.id === componentId
    )
  ) {
    throw new Error('This survey component is not available in the current experience.');
  }
  return surveyComponentDestination(compiled, screenId, componentId, interaction, context);
}

export function canonicalComponentConfig(
  compiled: CompiledSurvey,
  component: PublicSurveyDocument['screens'][number]['components'][number],
  context: SurveyRuntimeContext
) {
  const localization = compiled.snapshot.configuration.localization;
  const locale =
    typeof context.locale === 'string' && localization.supportedLocales.includes(context.locale)
      ? context.locale
      : localization.defaultLocale;
  return {
    ...blockDefaults.blocks[component.type as keyof typeof blockDefaults.blocks]?.config,
    ...component.config,
    ...component.translations?.[localization.defaultLocale]?.config,
    ...component.translations?.[locale]?.config,
  } as Record<string, unknown>;
}
